// User Settings Dialog

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, ColorScheme } from '../contexts/ThemeContext';
import { TwoFactorSetup } from './TwoFactorSetup';
import { authService } from '../api/authService';
import './UserSettings.css';

const COLOR_SCHEMES: { value: ColorScheme; label: string; icon: string; colors: string[] }[] = [
  { value: 'green', label: 'Vereinsfarbe', icon: '🌿', colors: ['#5a8c14', '#a0c419'] },
  { value: 'blue', label: 'Klassisch Blau', icon: '💙', colors: ['#667eea', '#764ba2'] },
  { value: 'purple', label: 'Lila', icon: '💜', colors: ['#8b5cf6', '#ec4899'] },
  { value: 'orange', label: 'Orange', icon: '🧡', colors: ['#ea580c', '#facc15'] },
];

interface UserSettingsProps {
  onClose: () => void;
}

export function UserSettings({ onClose }: UserSettingsProps) {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme, effectiveTheme, colorScheme, setColorScheme } = useTheme();
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [showExistingQr, setShowExistingQr] = useState(false);
  const [existingQrData, setExistingQrData] = useState<{ secretKey: string; qrCodeUri: string } | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Password Change State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handle2FASetupComplete = async () => {
    setShow2FASetup(false);
    if (refreshUser) {
      await refreshUser();
    }
  };

  const handle2FASetupCancel = () => {
    setShow2FASetup(false);
  };

  const handleShowExistingQr = async () => {
    try {
      setQrLoading(true);
      const data = await authService.getExisting2FAQrCode();
      setExistingQrData(data);
      setShowExistingQr(true);
    } catch (err) {
      console.error('QR-Code konnte nicht geladen werden', err);
    } finally {
      setQrLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validierung
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Bitte füllen Sie alle Felder aus');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Das neue Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setIsChangingPassword(true);

    try {
      await authService.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Passwort erfolgreich geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || 'Fehler beim Ändern des Passworts');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <>
      <div className="user-settings-modal" onClick={onClose}>
        <div className="user-settings-content" onClick={(e) => e.stopPropagation()}>
          <div className="user-settings-header">
            <h2>Benutzereinstellungen</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="user-settings-body">
            {/* Benutzerinformationen */}
            <section className="settings-section">
              <h3>Profil</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>E-Mail:</label>
                  <span>{user?.email}</span>
                </div>
                <div className="info-item">
                  <label>Name:</label>
                  <span>{user?.fullName}</span>
                </div>
                {user?.roles && user.roles.length > 0 && (
                  <div className="info-item">
                    <label>Rollen:</label>
                    <span>{user.roles.join(', ')}</span>
                  </div>
                )}
              </div>
            </section>

            {/* Darstellung / Theme */}
            <section className="settings-section">
              <h3>Darstellung</h3>

              {/* Hell/Dunkel Modus */}
              <div className="theme-selector">
                <p className="settings-description">
                  Wählen Sie Ihren bevorzugten Modus
                </p>
                <div className="theme-options">
                  <button
                    className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => setTheme('light')}
                    title="Helles Design"
                  >
                    <span className="theme-icon">☀️</span>
                    <span className="theme-label">Hell</span>
                  </button>
                  <button
                    className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => setTheme('dark')}
                    title="Dunkles Design"
                  >
                    <span className="theme-icon">🌙</span>
                    <span className="theme-label">Dunkel</span>
                  </button>
                  <button
                    className={`theme-option ${theme === 'system' ? 'active' : ''}`}
                    onClick={() => setTheme('system')}
                    title="Systemeinstellung verwenden"
                  >
                    <span className="theme-icon">💻</span>
                    <span className="theme-label">System</span>
                  </button>
                </div>
                <p className="theme-status">
                  Aktuelles Design: <strong>{effectiveTheme === 'dark' ? 'Dunkel' : 'Hell'}</strong>
                  {theme === 'system' && ' (automatisch)'}
                </p>
              </div>

              {/* Farbschema */}
              <div className="color-scheme-selector">
                <p className="settings-description">
                  Wählen Sie Ihr Farbschema
                </p>
                <div className="color-scheme-options">
                  {COLOR_SCHEMES.map((scheme) => (
                    <button
                      key={scheme.value}
                      className={`color-scheme-option ${colorScheme === scheme.value ? 'active' : ''}`}
                      onClick={() => setColorScheme(scheme.value)}
                      title={scheme.label}
                    >
                      <span
                        className="color-scheme-preview"
                        style={{
                          background: `linear-gradient(135deg, ${scheme.colors[0]} 0%, ${scheme.colors[1]} 100%)`
                        }}
                      />
                      <span className="color-scheme-label">{scheme.label}</span>
                    </button>
                  ))}
                </div>
                <p className="theme-status">
                  Aktuelles Farbschema: <strong>{COLOR_SCHEMES.find(s => s.value === colorScheme)?.label}</strong>
                </p>
              </div>
            </section>

            {/* Sicherheitseinstellungen */}
            <section className="settings-section">
              <h3>Sicherheit</h3>

              <div className="security-item">
                <div className="security-item-header">
                  <div>
                    <h4>Zwei-Faktor-Authentifizierung (2FA)</h4>
                    <p className="security-description">
                      Schützen Sie Ihr Konto mit einer zusätzlichen Sicherheitsebene
                    </p>
                  </div>
                  <div className="security-status">
                    <span className={`status-badge ${user?.isTwoFactorEnabled ? 'active' : 'inactive'}`}>
                      {user?.isTwoFactorEnabled ? '✓ Aktiviert' : '✗ Deaktiviert'}
                    </span>
                  </div>
                </div>

                {!user?.isTwoFactorEnabled ? (
                  <button
                    onClick={() => setShow2FASetup(true)}
                    className="btn-primary btn-small"
                  >
                    2FA aktivieren
                  </button>
                ) : (
                  <div>
                    <div className="info-box success" style={{ marginBottom: '12px' }}>
                      <p>Ihre Zwei-Faktor-Authentifizierung ist aktiv und schützt Ihr Konto.</p>
                    </div>
                    <button
                      onClick={handleShowExistingQr}
                      className="btn-secondary btn-small"
                      disabled={qrLoading}
                    >
                      {qrLoading ? 'Lädt...' : 'QR-Code für weiteres Gerät anzeigen'}
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* Passwort ändern */}
            <section className="settings-section">
              <h3>Passwort ändern</h3>

              <div className="security-item">
                <form onSubmit={handleChangePassword}>
                  {passwordError && <div className="error-message">{passwordError}</div>}
                  {passwordSuccess && <div className="success-message-text">{passwordSuccess}</div>}

                  <div className="form-group">
                    <label>Aktuelles Passwort</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="form-input"
                      disabled={isChangingPassword}
                    />
                  </div>

                  <div className="form-group">
                    <label>Neues Passwort</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="form-input"
                      disabled={isChangingPassword}
                    />
                  </div>

                  <div className="form-group">
                    <label>Neues Passwort bestätigen</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="form-input"
                      disabled={isChangingPassword}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary btn-small"
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? 'Wird geändert...' : 'Passwort ändern'}
                  </button>
                </form>
              </div>
            </section>
          </div>

          <div className="user-settings-footer">
            <button onClick={onClose} className="btn-secondary">
              Schließen
            </button>
          </div>
        </div>
      </div>

      {show2FASetup && (
        <TwoFactorSetup
          onComplete={handle2FASetupComplete}
          onCancel={handle2FASetupCancel}
        />
      )}

      {showExistingQr && existingQrData && (
        <ExistingQrModal
          secretKey={existingQrData.secretKey}
          qrCodeUri={existingQrData.qrCodeUri}
          onClose={() => { setShowExistingQr(false); setExistingQrData(null); }}
        />
      )}
    </>
  );
}

function ExistingQrModal({ secretKey, qrCodeUri, onClose }: { secretKey: string; qrCodeUri: string; onClose: () => void }) {
  const [qrDataUrl, setQrDataUrl] = React.useState('');

  React.useEffect(() => {
    import('qrcode').then(QRCode => {
      QRCode.toDataURL(qrCodeUri, { width: 240, margin: 2 }).then(setQrDataUrl).catch(console.error);
    });
  }, [qrCodeUri]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h3 className="modal-title">QR-Code für weiteres Gerät</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" style={{ textAlign: 'center' }}>
          <p style={{ marginBottom: '16px', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Scannen Sie diesen QR-Code mit dem Microsoft Authenticator (oder einer anderen TOTP-App) auf Ihrem zweiten Gerät.
          </p>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="2FA QR-Code" style={{ width: '240px', height: '240px', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }} />
          ) : (
            <div style={{ width: '240px', height: '240px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', borderRadius: '8px' }}>
              Lädt...
            </div>
          )}
          <div style={{ marginTop: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '6px', wordBreak: 'break-all' }}>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Manueller Schlüssel (falls Kamera nicht verfügbar):</p>
            <code style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '2px' }}>{secretKey}</code>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>Fertig</button>
        </div>
      </div>
    </div>
  );
}
